import { ReactElement } from 'react';
import { useSelector } from 'react-redux';
import { CustomColumn } from '../../../../../types/components/tables/tableTypes';
import { Fund } from '../../../../../types/server-data/ClientConfig';
import GridItem from '../../../../layout/GridComponents/GridItem';
import CustomTable from '../../../../tables/CustomTable';
import SecondLevelTable from './second-level-table/second-level-table.component';
import RaptorStatusBox from '../../../../feedback/RaptorStatusBox.component';
import PageLinkButton from '../../../../buttons/PageLinkButton';
import { specifyStatus } from '../../../../../utilities/generalMappings';
import NotesComp from '../../../sharedComponents/notes/Notes';
import { activeSectionSelector } from '../../../../../redux/pages/selectors';
import {
  SupplementaryRulesFirstTableConfig,
  SupplementaryRulesFirstTableRow,
  SupplementaryRulesLimitStatusRow,
  SupplementaryRulesPayload,
  SupplementaryRulesSecondTableConfig,
} from '../../../../../api/rs-hg/risk/supplementaryRules.types';
import RuleTimeSeriesCell from '../../shared/RuleTimeSeriesCell';

const buildFirstLevelColumns = (
  fundId: string,
  fundName: string,
  positionDate: string,
  section: string,
  firstTable: SupplementaryRulesFirstTableConfig,
): CustomColumn<SupplementaryRulesFirstTableRow>[] =>
  firstTable.columns.map((column) => {
    const baseColumn: CustomColumn<SupplementaryRulesFirstTableRow> = {
      title: column.column_header,
      field: column.column_key,
      headerStyle: {
        textAlign: 'center',
      },
      cellStyle: {
        textAlign: 'center',
      },
    };

    if (column.type === 'status') {
      return {
        ...baseColumn,

        render: (rowData) => (
          (() => {
            const rawStatus = String(rowData[column.column_key] ?? '');
            const status = specifyStatus(rawStatus);

            if (rowData.link) {
              return (
                <PageLinkButton
                  status={status}
                  text={rawStatus}
                  page={rowData.link}
                  section={section}
                  fundName={fundId}
                  classes={{
                    link: 'block w-full',
                    root: 'w-full !max-w-none',
                  }}
                />
              );
            }

            return (
              <RaptorStatusBox
                status={status}
                message={rawStatus}
              />
            );
          })()
        ),
      };
    }

    if (column.type === 'notes') {
      return {
        ...baseColumn,
        render: (rowData) => (
          <NotesComp
            positionDate={positionDate}
            fundId={fundId}
            fundName={fundName}
            subject={firstTable.noteSubject}
            topic={`${firstTable.noteTopicPrefix}${rowData.number}`}
          />
        ),
      };
    }

    return baseColumn;
  });

const timeSeriesColumn = (
  fundId: string,
): CustomColumn<SupplementaryRulesFirstTableRow> => ({
  title: 'Time Series',
  field: 'time_series',
  sorting: false,
  headerStyle: {
    textAlign: 'center',
  },
  cellStyle: {
    padding: '0.62rem 0',
    textAlign: 'center',
    width: '40%',
  },
  render: (rowData) => {
    const ruleKey =
      typeof rowData.rule_key === 'string' ? rowData.rule_key : '';
    return (
      <RuleTimeSeriesCell
        timeSeriesKey="supplementary"
        ruleParams={{ fundName: fundId, ruleKey }}
        chartId={`${fundId}-${ruleKey || 'rule'}`}
        enabled={!!fundId && !!ruleKey}
        initialPoints={
          rowData.historical_time_series?.length
            ? rowData.historical_time_series
            : undefined
        }
      />
    );
  },
});

export function buildFirstLevelSupplementaryRulesData(
  data: SupplementaryRulesPayload,
  firstTable: SupplementaryRulesFirstTableConfig,
): SupplementaryRulesFirstTableRow[] {
  // Backend `limit_status` is BE-driven object rows keyed by `column_key`.
  // This code intentionally does not implement legacy tuple parsing.
  const limitStatusRows = Array.isArray(data.limit_status)
    ? data.limit_status
    : [];

  const objectRows = limitStatusRows.filter(
    (row): row is SupplementaryRulesLimitStatusRow =>
      row != null && typeof row === 'object' && !Array.isArray(row),
  );

  return objectRows.map((limitStatusRow, index) => {
    const tableRow: SupplementaryRulesFirstTableRow = {
      number: index + 1,
      name: '',
      status: '',
    };

    firstTable.columns.forEach((column) => {
      if (column.type === 'row_number') {
        tableRow[column.column_key] = index + 1;
        return;
      }

      if (column.type === 'text' || column.type === 'status') {
        const value = limitStatusRow[column.column_key];

        tableRow[column.column_key] =
          typeof value === 'string' || typeof value === 'number' || value == null
            ? (value as any)
            : null;
      }
    });

    const link = limitStatusRow.link;
    if (typeof link === 'string' && link.trim()) {
      tableRow.link = link;
    }

    const ruleKey = limitStatusRow.rule_key;
    if (typeof ruleKey === 'string' && ruleKey.trim()) {
      tableRow.rule_key = ruleKey;
    }

    tableRow.historical_time_series =
      Array.isArray(limitStatusRow.time_series) &&
      limitStatusRow.time_series.length
        ? limitStatusRow.time_series
        : undefined;

    return tableRow;
  });
}

interface SupplementaryRulesFirstLevelTableProps {
  fundDetails: Fund;
  supplementaryRulesData: SupplementaryRulesPayload;
  selectedPositionDate: string;
  firstTable: SupplementaryRulesFirstTableConfig;
  secondTable: SupplementaryRulesSecondTableConfig;
}

function FirstLevelTable({
  fundDetails,
  supplementaryRulesData,
  selectedPositionDate,
  firstTable,
  secondTable,
}: SupplementaryRulesFirstLevelTableProps): ReactElement {
  const section = useSelector(activeSectionSelector) || '';
  const tableData = buildFirstLevelSupplementaryRulesData(
    supplementaryRulesData,
    firstTable,
  );
  const csvFields = firstTable.columns
    .filter((column) => column.type !== 'notes')
    .map((column) => column.column_key);

  const columns = buildFirstLevelColumns(
    fundDetails.id,
    fundDetails.name,
    selectedPositionDate,
    section,
    firstTable,
  );
  const notesIndex = columns.findIndex((column) => column.field === 'notes');
  const timeSeries = timeSeriesColumn(fundDetails.id);
  if (notesIndex >= 0) {
    columns.splice(notesIndex, 0, timeSeries);
  } else {
    columns.push(timeSeries);
  }

  return (
    <GridItem xs={12} card>
      <CustomTable<SupplementaryRulesFirstTableRow>
        selectedPositionDate={selectedPositionDate}
        columns={columns}
        csvFields={csvFields}
        showToolbar={true}
        data={tableData}
        options={{
          exportButton: true,
          search: true,
          paging: false,
          raptorExport: true,
        }}
        toolbarComponents={{
          toolbarTitle: fundDetails?.name,
        }}
        onRowClick={(event: any, rowData, togglePanel: any) => {
          return togglePanel!();
        }}
        detailPanel={[
          {
            render: (rowData) => {
              return (
                <SecondLevelTable
                  selectedPositionDate={selectedPositionDate}
                  rowData={rowData as SupplementaryRulesFirstTableRow}
                  supplementaryRulesData={supplementaryRulesData}
                  fundDetails={fundDetails}
                  secondTable={secondTable}
                />
              );
            },
          },
        ]}
      />
    </GridItem>
  );
}

export default FirstLevelTable;
